/**
 * The sidebar, as data: one source for every entry the product shows. Each entry is gated on the module its page's
 * endpoints resolve to on the server (W1-9: menu key = endpoint module); the client decides nothing else.
 * menu-items/dashboard.js turns this into menu items (icons, translations, the open-signals chip).
 */
export const CPV_MODULE = 'Quality Management';

/** D-11 follow-up: the module the parameter master endpoints resolve to on the server. */
export const CPV_MASTER_MODULE = 'CPV Master Data';

/** CPV entries [{id, title, url, show}] — `canRead`: the user's read flag on CPV_MODULE. */
export function cpvEntries(canRead, canReadMaster = false) {
  const can = Boolean(canRead);
  return [
    { id: 'cpv-home', title: 'Dashboard', url: '/cpv', show: can },
    { id: 'cpv-plans', title: 'Plans', url: '/cpv/plans', show: can },
    { id: 'cpv-signals', title: 'Signals', url: '/cpv/signals', show: can },
    { id: 'cpv-risk-bands', title: 'Risk bands', url: '/cpv/risk-bands', show: can },
    { id: 'cpv-master', title: 'CPV parameters', url: '/cpv/master', show: Boolean(canReadMaster) },
  ];
}

/**
 * G-03 dashboard entries [{id, title, url, show}] — gated on CPV_MODULE: the server resolves `^cpv/dashboards/` to the
 * `^cpv/` row of its permissions map (Quality Management read). The Portfolio entry is the CPV home (`cpv-home`).
 */
export function dashboardEntries(canRead) {
  const can = Boolean(canRead);
  return [
    { id: 'cpv-home', title: 'Portfolio', url: '/cpv', icon: 'LayoutGrid', show: can },
    { id: 'cpv-dash-signals', title: 'Signals', url: '/cpv/dashboards/signals', icon: 'BellRing', show: can },
    { id: 'cpv-dash-capability', title: 'Capability', url: '/cpv/dashboards/capability', icon: 'Gauge', show: can },
    { id: 'cpv-dash-plans', title: 'Plans & workflow', url: '/cpv/dashboards/plans', icon: 'ClipboardCheck', show: can },
    { id: 'cpv-dash-data-health', title: 'Data health', url: '/cpv/dashboards/data-health', icon: 'HeartPulse', show: can },
    { id: 'cpv-dash-management', title: 'Management', url: '/cpv/dashboards/management', icon: 'Briefcase', show: can },
    { id: 'cpv-dash-risk', title: 'Risk', url: '/cpv/dashboards/risk', icon: 'ShieldAlert', show: can },
  ];
}

/** ND-12: the module the data sheet, batch and my-actions endpoints resolve to on the server. */
export const CPV_SHEETS_MODULE = 'CPV Data Sheets';

/** Data sheet entries [{id, title, url, show}] — `canRead`: the read flag on CPV_SHEETS_MODULE; `canInbox`: My actions. */
export function sheetEntries(canRead, canInbox = canRead) {
  const can = Boolean(canRead);
  return [
    { id: 'cpv-sheets', title: 'Sheets', url: '/cpv/sheets', show: can },
    { id: 'cpv-batches', title: 'Batches', url: '/cpv/batches', show: can },
    // G-09: history imported from spreadsheets (CSV or XLSX) (the `^cpv/batches/` routes: this module)
    { id: 'cpv-history-imports', title: 'History imports', url: '/cpv/batches/imports', show: can },
    { id: 'my-actions', title: 'My actions', url: '/cpv/my-actions', show: Boolean(canInbox) },
  ];
}

/**
 * The whole menu: top-level items and one-level groups, in order. `ac(module)` → read flag; `anyOf(...modules)`.
 * Shape: { id, title, url, icon } for an item, { id, title, icon, children } for a group.
 */
export function menuEntries({ ac, anyOf, assistant = false, schedules = false, validationPack = false, auditTrail = false }) {
  const cpv = Object.fromEntries(cpvEntries(ac(CPV_MODULE), ac(CPV_MASTER_MODULE)).map((e) => [e.id, e]));
  const sheets = Object.fromEntries(sheetEntries(ac(CPV_SHEETS_MODULE), anyOf(CPV_SHEETS_MODULE, CPV_MODULE)).map((e) => [e.id, e]));
  return [
    { id: 'dashboards', title: 'Dashboard', icon: 'LayoutGrid', children: dashboardEntries(ac(CPV_MODULE)) },
    { id: 'plans', title: 'Plans', icon: 'ClipboardList', children: [
      { ...cpv['cpv-plans'], icon: 'ClipboardList' },
      { ...cpv['cpv-risk-bands'], icon: 'Gauge' },
    ] },
    { ...cpv['cpv-signals'], icon: 'BellRing', chip: 'openSignals' },
    // CPV-AI-01 AI-5: early-warning insights (`^cpv/insights/`: the `^cpv/` row, Quality Management read)
    { id: 'cpv-insights', title: 'Insights', url: '/cpv/insights', icon: 'Lightbulb', show: ac(CPV_MODULE) },
    // CPV-AI-01: shown when the server says the person holds POLICY cpv.ai.use (`assistant`: suggestions' `allowed`)
    { id: 'cpv-assistant', title: 'Assistant', url: '/cpv/assistant', icon: 'Sparkles', show: ac(CPV_MODULE) && Boolean(assistant) },
    { id: 'data-sheets', title: 'Data sheets', icon: 'FileStack', children: [
      { ...sheets['cpv-sheets'], icon: 'FileStack' },
      { ...sheets['cpv-batches'], icon: 'Boxes' },
      { ...sheets['cpv-history-imports'], icon: 'FileUp' },
    ] },
    { ...sheets['my-actions'], icon: 'Inbox', chip: 'myActions' },
    { id: 'reports', title: 'Reports', icon: 'FileText', children: [
      // G-05: gated as the plans entry is — the server resolves `^cpv/reports/` to the `^cpv/` row (Quality Management read)
      { id: 'cpv-reports', title: 'Periodic CPV reports', url: '/cpv/reports', icon: 'FileBarChart', show: ac(CPV_MODULE) },
      { id: 'record-exports', title: 'Certified exports', url: '/record-exports', icon: 'FileSignature',
        show: anyOf(CPV_MODULE, 'Audit Logs') },
      // G-43: the statistics check reads the plan's charts (`^cpv/plans/`: CPV_MODULE)
      { id: 'cpv-statistics-check', title: 'Statistics check', url: '/cpv/reports/statistics-check', icon: 'FileCheck2',
        show: ac(CPV_MODULE) },
      // CPV Q12-i: the signed periodic review of the CPV audit trail — shown when the server says the person holds POLICY
      // cpv.audit_trail.review at a plant in view (QA Reviewer, QA Manager); in the lite menu, so QA sees it
      { id: 'cpv-audit-trail', title: 'Audit trail review', url: '/cpv/audit-trail', icon: 'FileSearch',
        show: ac(CPV_MODULE) && Boolean(auditTrail) },
      { id: 'report-designer', title: 'Report Designer', url: '/report-designer', icon: 'LayoutTemplate',
        show: anyOf('Report', 'Report Designer', 'Report Designer Approval', 'Report Designer Export') },
      // Analytics Studio and Data Tables: gated on CPV_MODULE, the module every /bp/apqr/… endpoint resolves to.
      { id: 'graph-studio', title: 'Analytics Studio', url: '/analytics-studio', icon: 'ChartSpline', show: ac(CPV_MODULE) },
      { id: 'data-tables', title: 'Data Tables', url: '/data-tables', icon: 'Table2', show: ac(CPV_MODULE) },
      // CPV-Q6 S6: checklists are data tables with QA-approved template versions; each act is a POLICY the server asks
      { id: 'checklists', title: 'Checklists', url: '/checklists', icon: 'ListChecks', show: ac(CPV_MODULE) },
    ] },
    { id: 'master-data', title: 'Master data', icon: 'Database', children: [
      { ...cpv['cpv-master'], icon: 'ListTree' },
      { ...sheets['cpv-history-imports'], id: 'master-history-imports', icon: 'FileSpreadsheet' },
      { id: 'product', title: 'Products', url: '/prod-config', icon: 'Layers', show: ac('Product Master') },
      { id: 'plant-configuration', title: 'Plant', url: '/plant-configuration', icon: 'Factory', show: ac('Plant Configuration') },
      { id: 'master-config', title: 'Departments & units', url: '/master-config', icon: 'Building2', show: ac('General Config') },
    ] },
    { id: 'integrations', title: 'Integrations', icon: 'Plug', children: [
      { id: 'integration_status', title: 'Status', url: '/integrations/status', icon: 'HeartPulse', show: ac('System Integration') },
      { id: 'system_integration', title: 'Hub', url: '/system-integration', icon: 'Network', show: ac('System Integration') },
      { id: 'integration_connect', title: 'Integration Studio', url: '/integrations/connect', icon: 'Workflow', show: ac('System Integration') },
      { id: 'integration_lims', title: 'LIMS', url: '/integrations/lims', icon: 'FlaskConical', show: ac('System Integration') },
      { id: 'integration_qms', title: 'QMS', url: '/integrations/qms', icon: 'ShieldCheck', show: ac('System Integration') },
    ] },
    { id: 'administration', title: 'Administration', icon: 'Settings', children: [
      { id: 'user-management', title: 'Users', url: '/user-management', icon: 'Users', show: ac('User Management') },
      { id: 'Access Control', title: 'Roles & access', url: '/accessControl', icon: 'Lock', show: ac('Access Control') },
      { id: 'password-policy', title: 'Password policy', url: '/password-policy', icon: 'KeyRound', show: ac('Access Control') },
      { id: 'active-sessions', title: 'Active sessions', url: '/active-sessions', icon: 'Activity', show: ac('Access Control') },
      { id: 'session-history', title: 'Session history', url: '/session-history', icon: 'History', show: ac('Access Control') },
      { id: 'audit-logs', title: 'Audit logs', url: '/audit-logs', icon: 'ScrollText', show: ac('Audit Logs') },
      // CPV Q7: shown when the server says the person holds POLICY cpv.report_schedule.configure at a plant in view
      { id: 'cpv-report-schedules', title: 'Report schedules', url: '/cpv/report-schedules', icon: 'CalendarClock',
        show: ac(CPV_MODULE) && Boolean(schedules) },
      // CPV Q6-P6: shown when the server says the person holds POLICY cpv.validation_pack.read at a plant in view (QA)
      { id: 'cpv-validation-pack', title: 'Validation pack', url: '/cpv/validation-pack', icon: 'PackageCheck',
        show: ac(CPV_MODULE) && Boolean(validationPack) },
      // CPV-Q4f: every cpv.* setting at the plant in view (`^cpv/settings/`: the `^cpv/` row, Quality Management read); a
      // change is decided by the setting's POLICY row in the signed setter. Not in the lite menu (administration only).
      { id: 'cpv-method-settings', title: 'CPV method settings', url: '/cpv/method-settings', icon: 'SlidersHorizontal',
        show: ac(CPV_MODULE) },
      // owner 21 Oct 2026: the Rules registry — every run rule at the plant in view, read only (`^cpv/rules/`: the `^cpv/`
      // row, Quality Management read); in the lite menu, so QA sees it
      { id: 'cpv-rules-registry', title: 'Rules registry', url: '/cpv/rules', icon: 'BookOpen', show: ac(CPV_MODULE) },
      { id: 'audit-trail-review', title: 'Audit trail review', url: '/audit-trail-review', icon: 'FileSearch',
        show: anyOf(CPV_MODULE, 'Audit Logs') },
      { id: 'workflow-design', title: 'Template Manager', url: '/workflow-design', icon: 'Workflow',
        show: anyOf('Workflow Management', 'Access Control') },
      { id: 'appliction_setting', title: 'App settings', url: '/app-setting', icon: 'SlidersHorizontal', show: ac('Application Setting') },
      { id: 'company-settings', title: 'Company settings', url: '/company-settings', icon: 'Building', show: ac('Company Settings') },
    ] },
  ];
}

/**
 * Q14 · the lite menu profile. The ONE list of entries most people see: the leaf ids below (a group shows when one of
 * its entries does). Every other entry stays reachable by its URL (routes and POLICY are untouched) and in the menu
 * for the people the rule below names, or for anyone who turns on "Show all screens".
 */
export const LITE_MENU_IDS = [
  'cpv-home', 'cpv-plans', 'cpv-sheets', 'cpv-batches', 'cpv-history-imports', 'my-actions', 'cpv-signals',
  'cpv-reports', 'graph-studio', 'cpv-insights', 'cpv-assistant', 'cpv-audit-trail', 'cpv-rules-registry',
];

/**
 * The default full menu: anyone who can open a set-up screen, i.e. holds read on a module that only the people who
 * define or administer things reach (parameter master, products, plant, general config, integrations, users and roles,
 * app and company settings, templates). Everyone else starts on the lite menu.
 */
export const SETUP_MODULES = [
  CPV_MASTER_MODULE, 'Product Master', 'Plant Configuration', 'General Config', 'System Integration',
  'User Management', 'Access Control', 'Application Setting', 'Company Settings', 'Workflow Management',
];

/** True when the person's grants include a set-up module (`anyOf` as in menuEntries). */
export const holdsSetupGrant = (anyOf) => anyOf(...SETUP_MODULES);

/** Whether a menu entry id shows in the lite menu. */
export const inLiteMenu = (id) => LITE_MENU_IDS.includes(id);
